import { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';

import AddNewBrand from '@components/AddNewBrand';
import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import { toastError, toastSuccess } from '@components/Toast';

import useSingleFileUpload from '@hooks/useSingleFileUpload';

import { getBrands, getSeasons } from '@store/design/action';
import { getAllBrands, getAllSeasons } from '@store/reducers';

import { fetchCollectionPurpose } from '@services/collectionService';

import ApiClient from '@lib/apiClient';

import { checkUserType, isAdmin, isFashionDesigner, onPreventDefaultAll, printApiError } from '@utils/Util';

const onSetCollectionOwner = () => {
	if (checkUserType('FASHION_DESIGNER')) {
		return true;
	} else {
		return false;
	}
};
const CollectionAddAndEdit = ({ title, type, onSubmiCollection, onClose, collectionId }) => {
	const [brandModal, setBrandModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [designInfo, setDesignInfo] = useState({});
	const [isBuyerSelectionDisabled, setIsBuyerSelectionDisabled] = useState(false);
	const [tagList, setTagList] = useState([]);
	const [newBrandName, setNewBrandName] = useState('');
	const dispatch = useDispatch();
	const params = useParams();
	const id = params?.id || collectionId;
	const navigate = useNavigate();
	const selectedRef = useRef();
	const submitRef = useRef();
	const [image, setImage] = useState('');
	const { readFile } = useSingleFileUpload();
	const [imageData, setImageData] = useState(null);
	const [deletedCoverDocId, setDeletedCoverDocId] = useState(null);
	const [collectionPurposeList, setCollectionPurposeList] = useState([]);

	const handleImageChange = async (e) => {
		const file = e.target.files[0];
		setImage(URL.createObjectURL(file));
		if (file) {
			const fileStr = await readFile(file);
			const imageData = {
				name: file.name,
				base64Str: fileStr,
				docMimeType: file.type,
				print: true,
				documentType: 'PRODUCT_DESIGN'
			};
			setImageData(imageData);
		}
	};

	const handleDeleteImage = async () => {
		setDeletedCoverDocId(designInfo?.coverDocumentResponse?.id);
		setImage('');
		setImageData(null);
	};

	let brands = useSelector(getAllBrands);
	brands = brands.map((item) => {
		return {
			id: item.id,
			value: item.name,
			docUrl: item.brandLogoDocResponse.docUrl
		};
	});
	let seasons = useSelector(getAllSeasons);
	seasons = seasons.map((item) => {
		return {
			id: item.code,
			value: item.name
		};
	});

	const getCollectionPurpose = async () => {
		try {
			setIsLoading(true);
			const data = await fetchCollectionPurpose();
			setCollectionPurposeList(data);
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getCollectionPurpose();
	}, []);

	const resetValidation = (type) => {
		if (type === 'typeError') {
			setErrors({ ...errors, typeError: '' });
		} else if (type === 'brandError') {
			setErrors({ ...errors, brandError: '' });
		} else if (type === 'collectionPurposeError') {
			setErrors({ ...errors, collectionPurposeError: '' });
		} else {
			setErrors({ ...errors, collectionError: '' });
		}
	};
	const getCollectionDetails = async (collectionId) => {
		setIsLoading(true);
		await ApiClient.GET('getCollectionDetails', collectionId)
			.then(({ data }) => {
				setIsLoading(false);

				setDesignInfo({
					...designInfo,
					collectionName: data.name,
					brandName: data.brand ? data.brand : '',
					brandId: data.brandId ? data.brandId : null,
					isNitexCollection:
						'isNitexCollection' in data ? data.isNitexCollection : onSetCollectionOwner(),
					season: data.season ? data.season : '',
					isBuyerSelection: data?.isBuyerSelection,
					coverDocumentResponse: data?.coverDocumentResponse,
					willPublish: data?.willPublish,
					creationPurpose: {
						...data?.creationPurposeTagResponse,
						value: data?.creationPurposeTagResponse?.text
					}
				});
				setImage(data?.coverDocumentResponse?.docUrl);
				setIsBuyerSelectionDisabled(data?.isBuyerSelection);
				setTagList(data.tagResponseList || []);
			})
			.catch((error) => {
				let response = error.response;
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};
	useEffect(() => {
		dispatch(getBrands()).finally(() => {
			setIsLoading(false);
		});
		dispatch(getSeasons()).finally(() => {
			setIsLoading(false);
		});
		if (type !== 'ADD_NEW') {
			getCollectionDetails(id);
		} else {
			setDesignInfo({
				...designInfo,
				isNitexCollection: onSetCollectionOwner(),
				willPublish: isFashionDesigner() ? true : false
			});
		}
	}, []);

	useEffect(() => {
		selectedRef.current.focus();
		document.body.addEventListener('keydown', onKeyDown);
		return () => {
			document.body.removeEventListener('keydown', onKeyDown);
		};
	}, []);

	const onKeyDown = (e) => {
		onPreventDefaultAll(e, document.activeElement, submitRef.current);
	};

	const onItemClick = (item) => {
		resetValidation('brandError');
		if (item?.title === 'Brand') {
			setDesignInfo({
				...designInfo,
				brandId: item.id,
				brandName: item.value
			});
		}
	};

	const setNewBrandToSelectedBrand = async (id) => {
		if (id) {
			setIsLoading(true);
			await ApiClient.GET('getBrandInfo', id)
				.then(({ data }) => {
					setDesignInfo({
						...designInfo,
						brandId: data.id,
						brandName: data.name
					});
					setIsLoading(false);
				})
				.catch(({ response }) => {
					setIsLoading(false);
					toastError(response.data.message);
				});
		}
	};
	const triggerBrandModal = () => {
		setBrandModal(!brandModal);
	};

	const getComponentOptionsFromTagResponse = () => {
		return tagList.map((item) => {
			return {
				id: item.id,
				text: item.text,
				value: item.text,
				label: item.text
			};
		});
	};

	const onTagChange = (selected) => {
		let tags = selected.map((item) => {
			if (item.__isNew__) {
				return {
					text: item.label
				};
			} else {
				return {
					text: item.label,
					id: item.id
				};
			}
		});
		setTagList(tags);
	};

	const handleChange = (e) => {
		if (e.target.name === 'willPublish') {
			setDesignInfo({
				...designInfo,
				willPublish: e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null
			});
			resetValidation('typeError');
		} else if (e.target.name === 'isNitexCollection') {
			setDesignInfo({
				...designInfo,
				isNitexCollection: e.target.value === 'yes' ? false : e.target.value === 'no' ? true : null
			});
			resetValidation('typeError');
		} else {
			setDesignInfo({
				...designInfo,
				[e.target.id]: e.target.value
			});
			resetValidation('collectionError');
		}
	};
	const isValidForm = () => {
		if (!designInfo.collectionName) {
			setErrors({ ...errors, collectionError: 'Collection is required' });
			return false;
		}
		if (!designInfo.brandId) {
			setErrors({ ...errors, brandError: 'Brand is required' });
			return false;
		}
		if (!designInfo.creationPurpose?.value) {
			setErrors({ ...errors, collectionPurposeError: 'Purpose is required' });
			return false;
		}
		// if (!designInfo?.willPublish && isFashionDesigner()) {
		// 	setErrors({ ...errors, willPublishError: 'Will publish is required' });
		// 	return false;
		// }

		return true;
	};

	const onEdit = async () => {
		let body = {
			id,
			name: designInfo.collectionName,
			brandId: designInfo.brandId,
			isNitexCollection: designInfo.isNitexCollection,
			season: designInfo.season,
			tagRequestList: tagList,
			isBuyerSelection: designInfo.isBuyerSelection,
			coverDocumentDTO: imageData,
			deletedCoverDocId,
			creationPurpose: {
				text: designInfo?.creationPurpose?.value,
				id: designInfo?.creationPurpose?.id
			},
			willPublish: designInfo?.willPublish
		};
		if (!isAdmin() || !isFashionDesigner()) {
			delete body.willPublish;
		}
		await ApiClient.PUT('updateCollection', body)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					toastSuccess(data.message);
					setDeletedCoverDocId(null);
					onSubmiCollection && onSubmiCollection();
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};
	const onAddNew = async () => {
		let body = {
			name: designInfo.collectionName,
			brandId: designInfo.brandId,
			season: designInfo.season,
			tagRequestList: tagList,
			isNitexCollection: designInfo.isNitexCollection,
			isBuyerSelection: designInfo.isBuyerSelection,
			privacy: 'CUSTOM',
			creationPurpose: {
				text: designInfo?.creationPurpose?.value,
				id: designInfo?.creationPurpose?.id
			},
			willPublish: designInfo?.willPublish
		};
		if (!isAdmin() || !isFashionDesigner()) {
			delete body.willPublish;
		}
		await ApiClient.POST('addCollection', body)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					toastSuccess(data.message);
					navigate(`/collections/view/${data.id}`, {});
					onSubmiCollection && onSubmiCollection();
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onSubmitDesigns = () => {
		const isValid = isValidForm();
		if (!isValid) return;
		setIsLoading(true);
		if (type === 'ADD_NEW') {
			onAddNew();
		} else {
			onEdit();
		}
	};

	return (
		<>
			<Modal
				show={true}
				centered
				animation={true}
				backdrop="static"
				onHide={onClose}
				className="auto-width-popup collection-width-popup"
			>
				<div className="common-popup edit-collection-popup products-upload-modal shadow-24dp">
					<Modal.Header closeButton>
						<h3 className="semibold-16 mb-0">{title}</h3>
					</Modal.Header>
					<Modal.Body>
						<div className="common-popup-body">
							<div className="container px-0">
								<div className="row">
									<div className="col-md-12 pr-2">
										<div className="upload-img mb-2">
											<div className="file-uploader small upload-design-image mb-4 mb-sm-0">
												<label htmlFor="drag-upload" className="drag-upload" />
												<input
													type="file"
													className="file-upload"
													name="drag-upload"
													id="drag-upload"
													accept="image/jpeg, image/jpg, image/png"
													onChange={handleImageChange}
												/>
											</div>
											{image && (
												<div
													className="file-editing"
													onClick={() => {
														handleDeleteImage();
													}}
												>
													<img src={image} alt="edit" />
													<div className="action-icon cursor-pointer">
														<CommonImage
															src="/icons/delete_gray.svg"
															alt="check"
															width="20px"
															isStatic={true}
														/>
													</div>
												</div>
											)}
										</div>
									</div>
									<div className="col-md-12">
										<div className="form-group mb-2">
											<label htmlFor="strengthList">Collection</label>
											<input
												type="text"
												autoComplete="off"
												name="collectionName"
												id="collectionName"
												value={designInfo.collectionName}
												onChange={handleChange}
												placeholder="Collection name"
												ref={selectedRef}
											/>
											{errors.collectionError && (
												<p className="error">{errors.collectionError}</p>
											)}
										</div>
									</div>
									<div className="col-md-6 pr-1 mb-2">
										<label htmlFor="collection">Brand</label>
										<CustomDropdown
											type="addItem"
											title="Brand"
											onItemClick={(value) => {
												if (value.isAddNew) {
													triggerBrandModal();
													setNewBrandName(value.value);
												} else {
													onItemClick(value);
												}
											}}
											items={brands}
											selectedItem={designInfo?.brandName}
											// isAddNew={true}
										/>
										{errors.brandError && <p className="error">{errors.brandError}</p>}
									</div>
									<div className="col-md-6">
										<div className="form-group select-component">
											<label htmlFor="market">Season</label>
											<select
												className="w-100"
												name="season"
												id="season"
												onChange={handleChange}
												value={designInfo.season}
											>
												<option>Select</option>
												{seasons.map((item) => (
													<option value={item.id} key={item.id}>
														{item.value}
													</option>
												))}
											</select>

											{/* {errors.seasonError && (
												<p className="error">{errors.seasonError}</p>
											)} */}
										</div>
									</div>
									<div className="col-md-6">
										<div className="form-group">
											<label htmlFor="market">Creation purpose?*</label>
											<CustomDropdown
												type="addItem"
												title="Select"
												items={collectionPurposeList}
												selectedItem={designInfo?.creationPurpose?.value}
												onItemClick={(value) => {
													setDesignInfo({
														...designInfo,
														creationPurpose: value
													});
												}}
												isAddNew={isAdmin()}
											/>
											{errors.collectionPurposeError && (
												<p className="error">{errors.collectionPurposeError}</p>
											)}
										</div>
									</div>
									<div className="col-md-6 pr-1">
										<div className="form-group no-shadow-react-select">
											<label htmlFor="market">Tag</label>
											<CreatableSelect
												value={getComponentOptionsFromTagResponse()}
												isMulti
												onChange={(selected) => onTagChange(selected)}
											/>
										</div>
									</div>
									<div
										className={`col-md-12 ${isFashionDesigner() || isAdmin() ? '' : 'd-none'}`}
									>
										<div className="form-group radio-component">
											<label
												htmlFor="yes"
												className="gray_dark_01 collection-radio-label"
											>
												Will Publish?
											</label>
											<input
												type="radio"
												id="willPublish"
												name="willPublish"
												value={'yes'}
												checked={designInfo.willPublish === true}
												onChange={handleChange}
											/>
											<label htmlFor="yes" className="gray_dark_01">
												Yes
											</label>
											<input
												type="radio"
												id="willPublish"
												name="willPublish"
												value={'no'}
												checked={designInfo.willPublish === false}
												onChange={handleChange}
											/>
											<label htmlFor="no" className="gray_dark_01">
												No
											</label>
											{errors.willPublishError && (
												<p className="error">{errors.willPublishError}</p>
											)}
										</div>
									</div>
									<div className="col-md-12">
										<div className="custom-chekbox">
											<div className="form-group m-0">
												{isBuyerSelectionDisabled ? (
													<input
														type="checkbox"
														checked={designInfo?.isBuyerSelection === true}
														disabled={true}
													/>
												) : (
													<input
														type="checkbox"
														id="isBuyerSelection"
														name="isBuyerSelection"
														checked={designInfo?.isBuyerSelection === true}
														onChange={() => {
															setDesignInfo({
																...designInfo,
																isBuyerSelection: !designInfo.isBuyerSelection
															});
														}}
													/>
												)}
												<label htmlFor="isBuyerSelection" className="m-0">
													<span className="align-middle">
														This collection is the buyer&#39;s selection.
														{designInfo?.isBuyerSelection}
													</span>
												</label>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Modal.Body>
					<div className="modal-footer">
						<div className="footer-buttons text-right">
							<button className="button gray-outline size32" onClick={onClose}>
								<span>Cancel</span>
							</button>
							<button
								className="button size32 ml-2"
								onClick={() => onSubmitDesigns()}
								disabled={isLoading}
								ref={submitRef}
							>
								Submit
							</button>
						</div>
					</div>
				</div>
			</Modal>
			<Modal show={brandModal} onHide={() => triggerBrandModal} size="lg" centered>
				<AddNewBrand
					brandModal={brandModal}
					triggerBrandModal={triggerBrandModal}
					setNewBrandToSelectedBrand={setNewBrandToSelectedBrand}
					brandName={newBrandName}
				/>
			</Modal>
		</>
	);
};

export default CollectionAddAndEdit;
